import { useEffect } from "react";
import { setLangMy } from "../redux/sys-slice";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from './ReduxHooks';

const useLanguageNavigator = (availableLanguages: string[], defaultLanguage = "en") => {
    const dispatch = useDispatch();
    const lang = useSelector(state => state.sys.language)

    // Helper function to safely get the language from localStorage
    const getSavedLanguage = () => {
        if (typeof window !== "undefined") {
            return localStorage.getItem("preferredLanguage");
        }
        return null;
    };

    // Helper function to safely get the browser language
    const getBrowserLanguage = () => {
        if (typeof window !== "undefined") {
            const browserLanguage = navigator.language || navigator.languages[0]; // e.g., "en-US"
            const primaryLanguage = browserLanguage.split("-")[0]; // Extract the primary language, e.g., "en"
            return primaryLanguage;
        }
        return defaultLanguage;
    };

    // Function to set the language in Redux and localStorage
    const setLanguage = (lang: string) => {
        dispatch(setLangMy(lang));
        if (typeof window !== "undefined") {
            localStorage.setItem("preferredLanguage", lang);
        }
    };

    // Initialize language
    useEffect(() => {
        const savedLanguage = getSavedLanguage();
        if (savedLanguage && availableLanguages.includes(savedLanguage)) {
            setLanguage(savedLanguage);
        } else {
            const browserLanguage = getBrowserLanguage();
            const userLanguage = availableLanguages.includes(browserLanguage)
                ? browserLanguage
                : defaultLanguage;

            if (!savedLanguage) {
                setLanguage(userLanguage);
            }
        }
    }, [availableLanguages, defaultLanguage]);

    // Function to manually update the language
    const updateLanguage = (newLanguage: string) => {
        if (availableLanguages.includes(newLanguage)) {
            setLanguage(newLanguage);
        }
    };

    return { lang, updateLanguage };
};

export default useLanguageNavigator;